import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from '@phosphor-icons/react';
import { useLang } from '@/context/LanguageContext';
import { UI_TEXT } from '@/data/tradeData';

const HERO_BG = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBlbGVjdHJpYyUyMGJyZWFrZXIlMjBwYW5lbCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzkyMjgwMjk1fDA&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: '110%', opacity: 0 },
  visible: (i) => ({
    y: '0%',
    opacity: 1,
    transition: { duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function HomeHero({ onOpenWizard }) {
  const { lang } = useLang();
  const t = UI_TEXT[lang].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end pb-20 sm:pb-28 overflow-hidden">
      {/* Parallax BG */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_BG} alt="" aria-hidden="true" className="w-full h-[120%] object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
        {/* Line-by-line masked reveal */}
        <div className="overflow-hidden">
          {[t.line1, t.line2, t.line3].map((line, i) => (
            <div key={i} className="overflow-hidden">
              <motion.h1 custom={i} variants={lineReveal} initial="hidden" animate="visible"
                className={`font-display font-black tracking-tighter leading-[0.92] ${i === 2 ? 'text-[#E31937]' : 'text-white'} text-4xl sm:text-6xl lg:text-8xl`}>
                {line}
              </motion.h1>
            </div>
          ))}
        </div>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }}
          className="text-[#A3A3A3] text-base sm:text-lg mt-6 max-w-lg">
          {lang === 'es' ? 'Para proyectos grandes y pequeños.' : 'For projects big and small.'}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 }} className="mt-10">
          <button onClick={onOpenWizard} data-testid="hero-cta"
            className="bg-[#E31937] text-white font-bold uppercase tracking-wider px-10 py-5 hover:bg-[#FF2445] transition-colors text-sm">
            {t.cta}
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
          className="mt-16 flex items-center gap-3 text-[#A3A3A3] text-xs uppercase tracking-widest">
          <ArrowDown size={14} className="animate-bounce" /> <span>{t.scroll}</span>
        </motion.div>
      </div>
    </section>
  );
}
